import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { tap, take } from 'rxjs/operators';
import * as fromAuthSelectors from '../../features/auth/selectors';
import * as authActions from '../../features/auth/actions';
import { SessionStorageIds } from '../../shared/enums';
import { EMPTY_GUID } from '../../shared/constants';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshInProgress = false;

  constructor(private store: Store) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let authKey = null;

    // Read the CURRENT value of each selector and let go immediately. Store selectors
    // replay their latest value synchronously on subscribe, so `take(1)` gets that value
    // and completes the subscription itself - nothing is left running afterwards.
    //
    // This used to be five plain `.subscribe()` calls added to a `Subscription` that was
    // only ever cleared in `ngOnDestroy`. Since this interceptor is a singleton that lives
    // for the whole app session, `ngOnDestroy` never ran in practice, so every single HTTP
    // request left 5 more live subscriptions behind - thousands of them over a session.
    this.store.select(fromAuthSelectors.fromRegisterSelector.getRegistrationSuccess).pipe(take(1)).subscribe(key => {
      if (key?.accessToken) {
        authKey = key?.accessToken;
      }
    });

    this.store.select(fromAuthSelectors.fromSignUpSelectors.getSignupSuccess).pipe(take(1)).subscribe(key => {
      if (key?.accessToken) {
        authKey = key?.accessToken;
      }
    });

    this.store.select(fromAuthSelectors.fromForgotPasscodeLoginConfirmSelectors.getForgotPasscodeVerifyResponse).pipe(take(1)).subscribe(key => {
      if (key?.accessToken) {
        authKey = key?.accessToken;
      }
    });

    this.store.select(fromAuthSelectors.fromSwitchToNewBusinessSelector.getSwitchToAntherBusinessProfileResponse).pipe(take(1)).subscribe(key => {
      if (key?.accessToken) {
        authKey = key?.accessToken;
      }
    });

    this.store.select(fromAuthSelectors.fromRefreshTokenSelector.getRefreshTokenResponse).pipe(take(1)).subscribe(key => {
      if (key?.accessToken) {
        authKey = key?.accessToken;
      }
    });

    var temp_token = sessionStorage.getItem('temp_token');
    var token = sessionStorage.getItem('token');
    const bearerToken = authKey ? authKey : (temp_token?temp_token:token);
    const businessId = sessionStorage.getItem('businessId');
    const headers: { [key: string]: string } = {
      Authorization: `Bearer ${bearerToken}`
    };
    if (businessId && businessId.trim() !== '') {
      headers['X-Legal-Entity-Id'] = businessId;
    }
    request = request.clone({
      setHeaders: headers
    });

    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.checkPermissionVersion(event, bearerToken);
        }
      })
    );
  }

  private checkPermissionVersion(response: HttpResponse<any>, currentToken: string | null): void {
    const serverVersion = response.headers.get('X-Permissions-Version');
    if (!serverVersion || !currentToken) {
      return;
    }

    // Skip if this response is from the validateToken call itself to avoid loops
    if (response.url && response.url.includes('validateToken')) {
      sessionStorage.setItem(SessionStorageIds.PERMISSIONS_VERSION, serverVersion);
      return;
    }

    const storedVersion = sessionStorage.getItem(SessionStorageIds.PERMISSIONS_VERSION);

    if (storedVersion !== null && storedVersion !== serverVersion && !this.refreshInProgress) {
      this.refreshInProgress = true;
      sessionStorage.setItem(SessionStorageIds.PERMISSIONS_VERSION, serverVersion);

      this.store.dispatch(authActions.loadValidateToken({
        request: {
          token: currentToken,
          uniqueDeviceId: EMPTY_GUID
        }
      }));

      setTimeout(() => { this.refreshInProgress = false; }, 5000);
    } else if (storedVersion === null) {
      sessionStorage.setItem(SessionStorageIds.PERMISSIONS_VERSION, serverVersion);
    }
  }
}
